import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Clock, Globe, Mail, Phone } from 'lucide-react';
import { api, auth } from '../lib/api';
import { getRememberedBrand } from '../lib/brand';
import { usePortalContext } from '../hooks/usePortalContext';
import { useAsync } from '../hooks/usePortal';

export type LegalKind = 'privacy_policy' | 'disclaimer' | 'contact';

interface BrandLegal {
  slug: string;
  name?: string;
  policies?: { disclaimer?: string | null; privacy_policy?: string | null };
  contact?: { website?: string | null; contactEmail?: string | null; contactNumber?: string | null };
}

const TITLES: Record<LegalKind, string> = {
  privacy_policy: 'Privacy Policy',
  disclaimer: 'Disclaimer',
  contact: 'Contact Us',
};

/** House brand for a device that has never resolved a program brand. */
const HOUSE_BRAND_SLUG = 'alliance_abroad';

/**
 * Legal / contact surface, per brand. The content is the brand's PUBLISHED
 * legal text and public contact details, served live from Globus through the
 * BFF's public /legal/:slug route (BOP #1338) — never hardcoded copy. Renders
 * both inside the app shell (signed in) and standalone (from the sign-in
 * screen); brand resolution mirrors the pre-auth theming: resolved context
 * brand → device-remembered brand → the house brand.
 */
export default function LegalPage({ kind }: { kind: LegalKind }) {
  const navigate = useNavigate();
  const { ctx } = usePortalContext();
  const slug = ctx?.brand?.slug ?? getRememberedBrand()?.slug ?? HOUSE_BRAND_SLUG;

  const { data, loading, error } = useAsync<BrandLegal>(
    () => api<BrandLegal>(`/legal/${encodeURIComponent(slug)}`),
    [slug],
  );

  // navigate(-1) leaves the SPA if the user landed here directly (no in-app
  // history), so fall back to wherever this visitor came from conceptually.
  const goBack = () => {
    if (window.history.state?.idx > 0) navigate(-1);
    else navigate(auth.getToken() ? '/dashboard' : '/sign-in');
  };

  const body = kind === 'contact' ? undefined : data?.policies?.[kind];

  return (
    <>
      <button type="button" className="backbtn" onClick={goBack}>
        <ArrowLeft size={18} aria-hidden />
        Back
      </button>

      <h1 style={{ marginBottom: 4 }}>{TITLES[kind]}</h1>
      {data?.name && <p style={{ margin: '0 0 16px', color: 'var(--ink-2)' }}>{data.name}</p>}

      {loading && <p role="status">Loading…</p>}
      {error && (
        <div className="card alertcard" role="alert">
          <h2>We couldn't load this page</h2>
          <p>{error}</p>
        </div>
      )}

      {!loading && !error && kind !== 'contact' && (
        body ? (
          <div className="card legalbody">{body}</div>
        ) : (
          <NotPublished what={`The ${TITLES[kind].toLowerCase()}`} />
        )
      )}

      {!loading && !error && kind === 'contact' && <ContactCard data={data} />}
    </>
  );
}

/** Unauthored / unpublished policy — the endpoint answers 200 with a null body. */
function NotPublished({ what }: { what: string }) {
  return (
    <div className="card comingsoon">
      <span className="comingsoon-icon" aria-hidden>
        <Clock size={28} />
      </span>
      <h2 style={{ margin: 0 }}>Not published yet</h2>
      <p style={{ margin: 0, color: 'var(--ink-2)', maxWidth: 460 }}>
        {what} for your program hasn't been published yet. Please check back soon.
      </p>
    </div>
  );
}

function ContactCard({ data }: { data?: BrandLegal }) {
  const { website, contactEmail, contactNumber } = data?.contact ?? {};
  if (!website && !contactEmail && !contactNumber) {
    return <NotPublished what="Contact information" />;
  }
  // Stored websites may lack a scheme; an unschemed href would resolve
  // relative to the portal origin.
  const href = website && !/^https?:\/\//i.test(website) ? `https://${website}` : website;
  return (
    <div className="card">
      <p style={{ marginTop: 0, color: 'var(--ink-2)' }}>
        Questions about your program? Here's how to reach {data?.name ?? 'us'}.
      </p>
      {contactEmail && (
        <p className="contactrow">
          <Mail size={18} aria-hidden />
          <a href={`mailto:${contactEmail}`}>{contactEmail}</a>
        </p>
      )}
      {contactNumber && (
        <p className="contactrow">
          <Phone size={18} aria-hidden />
          <a href={`tel:${contactNumber.replace(/[^+\d]/g, '')}`}>{contactNumber}</a>
        </p>
      )}
      {website && (
        <p className="contactrow">
          <Globe size={18} aria-hidden />
          <a href={href ?? undefined} target="_blank" rel="noopener noreferrer">{website}</a>
        </p>
      )}
    </div>
  );
}
